// src/web/Collaborations.jsx

import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageTracker from '../../components/tracking/PageTracker';
import ScrollTracker from '../../components/tracking/ScrollTracker';

function Collaborations() {
  const { t } = useTranslation();

  return (
    <div className="collaborations-page">
      <PageTracker pageName="Collaborations" />

      {/* HERO */}
      <section className="collab-hero">
        <div className="collab-hero-content">
          <h1>
            {t('collab_title', 'Evidence & Research Collaborations')}
          </h1>

          <p>
            <Trans i18nKey="collab_intro">
              All-Voiced evolves through collaborations with researchers,
              educators, and institutions. These collaborations help evaluate
              educational approaches, generate evidence, and inform the
              development of structured training environments for
              auditory-perceptual voice evaluation.
            </Trans>
          </p>
        </div>
      </section>

      {/* PUBLICATIONS */}
      <section className="collab-section">
        <h2>
          {t('collab_active_title', 'Peer-reviewed publications')}
        </h2>

        <ul className="collab-cards">

          <li className="collab-card">
            <a
              href="https://doi.org/10.1016/j.jvoice.2025.11.029"
              target="_blank"
              rel="noopener noreferrer"
              className="collab-card-link"
            >
              <span className="collab-meta">
                Journal of Voice · 2025 · Open Access
              </span>

              <h3>
                Improving Auditory-Perceptual Voice Assessment:
                A Hybrid In-Class and Online CAPE-V Training Study
              </h3>

              <p>
                This study evaluated a <strong>hybrid auditory-perceptual
                training approach</strong> combining expert-led in-class
                discussion with optional online practice using
                <strong> All-Voiced</strong>.
              </p>

              <p>
                The training aimed to improve the accuracy, reliability, and
                generalization of CAPE-V ratings in speech-language pathology
                students.
              </p>

              <p>
                Results showed significant improvements after training, with 
                gains maintained over time. The study also demonstrated how 
                structured online practice can extend learning beyond the 
                classroom while supporting continued perceptual calibration.
              </p>
            </a>

            <div className="collab-citation">
              <p>
                <strong>
                  Pommée, T., Younes, S., Gauthier, L., & Verduyckt, I.
                </strong>{' '}
                (2025).
                <em>
                  {' '}Improving auditory-perceptual voice assessment: A hybrid
                  in-class and online CAPE-V training study.
                </em>{' '}
                <i>Journal of Voice.</i>{' '}
                <a
                  href="https://doi.org/10.1016/j.jvoice.2025.11.029"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://doi.org/10.1016/j.jvoice.2025.11.029
                </a>
              </p>
            </div>
          </li>
        </ul>

        <p className="collab-note">
          This publication represents the first peer-reviewed educational study
          involving All-Voiced.
        </p>
      </section>

      {/* CONNECTION TO INSTITUTIONAL PROGRAMS */}
      <section className="collab-section collab-context">
        <h2>
          {t('collab_context_title', 'From evidence to educational programs')}
        </h2>

        <p>
          This line of work informs the development of All-Voiced Institutional
          Programs, where structured listening activities can support training,
          authentic assessment, learning analytics, competency development, and
          educational research.
        </p>

        <div className="collab-actions web-cta-row">
          <Link
            to="/institutional-programs"
            className="web-button web-button--primary"
          >
            Explore institutional programs
          </Link>

          <Link
            to="/insights/authentic-assessment-ai-era"
            className="web-button web-button--secondary"
          >
            Read the related insight
          </Link>
        </div>
      </section>

      <ScrollTracker pageName="Collaborations" />
    </div>
  );
}

export default Collaborations;
